import React, { RefObject, useState } from 'react';
import { useCommunityContext } from 'components/CommunityContext';
import { useMutation } from '@apollo/client';
import {
  EVENT_POST_TYPES,
  SUCCESS,
  FAILURE,
  NUMBER_OF_REACTIONS,
} from 'utils/constants';
import { rawId } from 'utils/rawId';
import { REPLY_FRAGMENT } from 'utils/queries';
import { genericToastAlert } from 'utils/toastHelpers';
import { sanitizeAttachments } from 'helpers/postHelpers';
import { CREATE_REPLY_MUTATION } from 'schema/Replies/mutations';
import { POST_QUERY } from 'schema/Post/queries';
import { areEqualSets } from 'helpers/setHelpers';
import { TFunction } from 'next-i18next';
import { RequestStatus } from 'types/request';
import { CurrentUser } from 'types/user';
import { CreateReplyMutationVariables } from '__generated__/graphql/legacy/graphql';
import { Attachment } from 'schema/Post/types';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';

interface CommentProps {
  cancelComment: () => void;
  color: string;
  value: string;
  attachment: Attachment | null;
  handleInputChange: (input: string) => void;
  handleMentionsChange: (
    _event: unknown,
    newValue: string,
    _field: unknown,
    mentions: Array<{ id: string }>
  ) => void;
  handleAttachmentChange: (attachment: Attachment) => void;
  handleAttachmentAltTextChange: (input: string) => void;
  submitComment: () => Promise<RequestStatus>;
  isSubmitDisabled: boolean;
  t: TFunction;
}

interface AddCommentContainerProps {
  communityWrapperRef?: RefObject<HTMLElement>;
  lastCommentRef?: RefObject<HTMLElement>;
  parentPostId: string;
  parentReplyId?: string;
  renderChildren: (
    props: CommentProps,
    user: CurrentUser
  ) => React.ReactElement;
  t: TFunction;
  onCommentSubmitted: () => void;
}

const AddCommentContainer: React.FC<AddCommentContainerProps> = ({
  communityWrapperRef,
  parentPostId,
  parentReplyId = null,
  t,
  renderChildren,
  lastCommentRef,
  onCommentSubmitted,
}) => {
  const [commentContent, setCommentContent] = useState('');

  const [commentAttachment, setCommentAttachment] = useState<Attachment | null>(
    null
  );

  const [commentAttachmentAltText, setCommentAttachmentAltText] = useState('');
  const [usersMentioned, setUsersMentioned] = useState([]);
  const [createReplyMutation, { loading }] = useMutation(CREATE_REPLY_MUTATION);
  const { currentUser, community, trackEvent } = useCommunityContext();

  const handleAttachmentChange = (attachment) => {
    setCommentAttachment(attachment);
  };

  const handleMentionsChange = (_event, newValue, _field, mentions) => {
    const mentionIds = mentions?.map((mention) => mention?.id);
    if (!areEqualSets(mentionIds, usersMentioned)) {
      setUsersMentioned(mentionIds);
    }

    setCommentContent(newValue);
  };

  const handleAttachmentAltTextChange = (altText) => {
    setCommentAttachmentAltText(altText);
  };

  const scrollToLastComment = () => {
    if (lastCommentRef && lastCommentRef.current) {
      const lastCommentNode = lastCommentRef.current;
      if (communityWrapperRef?.current) {
        communityWrapperRef.current.scrollTo(0, lastCommentNode.offsetTop);
      }
    }
  };

  const submitComment = async () => {
    let requestStatus;
    const variables: CreateReplyMutationVariables = {
      discussionableId: community.id,
      content: commentContent,
      [parentReplyId ? 'parentReplyId' : 'parentPostId']:
        parentReplyId || parentPostId,
      postAttachmentInput: sanitizeAttachments(
        commentAttachment
          ? {
              ...commentAttachment,
              altText: commentAttachmentAltText,
            }
          : null
      ),
      numberOfReactions: NUMBER_OF_REACTIONS,
    };

    if (usersMentioned?.length) {
      variables.mentionedUserIds = usersMentioned;
    }

    if (commentContent || commentAttachment) {
      try {
        const {
          data: createReplyData,
          errors: createReplyError,
        } = await createReplyMutation({
          variables,
          refetchQueries: [
            {
              query: POST_QUERY,
              variables: {
                communityId: community.id,
                postId: parentPostId,
                numberOfReactions: NUMBER_OF_REACTIONS,
              },
            },
          ],
          update(cache, { data: { createReply } }) {
            const { reply } = createReply;
            const parentObject = {
              id: parentReplyId || parentPostId,
              __typename: parentReplyId ? 'Reply' : 'Post',
            };
            cache.modify({
              id: cache.identify(parentObject),
              fields: {
                replies(existingReplies = {}) {
                  const newReplyRef = cache.writeFragment({
                    data: reply,
                    fragment: REPLY_FRAGMENT,
                    fragmentName: 'ReplyFields',
                  });
                  const newEdges = [
                    ...(existingReplies.edges || []),
                    { node: newReplyRef },
                  ];
                  const newTotalCount = existingReplies.totalCount + 1;
                  return {
                    ...existingReplies,
                    totalCount: newTotalCount,
                    edges: newEdges,
                  };
                },
              },
            });
          },
        });

        const {
          ATTACHMENT,
          POST_TYPE,
          POST_ID,
          POST_ID_ENCODED,
          HAS_MENTION,
        } = TrackingProperty;

        trackEvent(TrackingEvent.POST_CREATED, {
          [ATTACHMENT]: commentAttachment
            ? commentAttachment.attachmentType
            : null,
          [POST_ID]: parentReplyId ? rawId(parentReplyId) : rawId(parentPostId),
          [POST_ID_ENCODED]: parentReplyId || parentPostId,
          [POST_TYPE]: parentReplyId
            ? EVENT_POST_TYPES.REPLY
            : EVENT_POST_TYPES.COMMENT,
          [HAS_MENTION]: !!usersMentioned?.length,
        });
        onCommentSubmitted?.();
        setCommentContent('');
        setCommentAttachment(null);
        setCommentAttachmentAltText('');
        scrollToLastComment();
        setUsersMentioned([]);

        const userErrors = createReplyData?.createReply?.userErrors;
        requestStatus = SUCCESS;
        if (userErrors?.length || createReplyError?.length) {
          requestStatus = FAILURE;
          genericToastAlert();
        }
      } catch {
        genericToastAlert();
        requestStatus = FAILURE;
      }
    }

    return requestStatus;
  };

  const cancelComment = () => {
    if (commentContent) {
      if (
        // eslint-disable-next-line no-alert
        window.confirm(t('Are you sure you want to discard your changes?'))
      ) {
        // eslint-disable-next-line no-new
        new Promise<void>((resolve) => {
          setCommentContent('');
          setCommentAttachment(null);
          setCommentAttachmentAltText('');
          resolve();
        });
      }
    }
  };

  const inputProps = {
    cancelComment,
    color: community.primaryColor,
    value: commentContent,
    attachment: commentAttachment,
    handleInputChange: setCommentContent,
    handleMentionsChange,
    handleAttachmentChange,
    handleAttachmentAltTextChange,
    submitComment,
    isSubmitDisabled: loading,
    t,
  };

  return renderChildren(inputProps, currentUser);
};

export default AddCommentContainer;
